import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Navigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import { useContext } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FileText, Search } from 'lucide-react';

const STORAGE_KEY = 'admin_commissions_list';

const defaultCommissions = [
  {
    id: 'c1',
    date: new Date().toISOString(),
    affiliate: 'aff_123',
    referred_user: 'user_567',
    amount: 39.0,
    status: 'pending',
    source: 'invoice'
  },
  {
    id: 'c2',
    date: new Date().toISOString(),
    affiliate: 'aff_999',
    referred_user: 'user_888',
    amount: 120.0,
    status: 'paid',
    source: 'referral'
  }
];

export default function AdminCommissions() {
  const { user } = useContext(AuthContext);
  const [commissions, setCommissions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    setCommissions(stored ? JSON.parse(stored) : defaultCommissions);
  }, []);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return commissions;
    return commissions.filter((c) => c.status === statusFilter);
  }, [commissions, statusFilter]);

  if (!user?.is_internal_admin && user?.role !== 'admin') {
    return <Navigate to="/app/admin" replace />;
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">Commissions</h1>
          <p className="text-[#8A93A6]">View all affiliate commissions and statuses</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] bg-[#1C1F26] border-white/10 text-white">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="reversed">Reversed</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="border-white/10 text-white flex items-center gap-2">
            <Search className="h-4 w-4" />
            Search (coming soon)
          </Button>
        </div>
      </div>

      <Card className="glass">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Commission List
          </CardTitle>
          <CardDescription>Pending, approved, paid, and reversed commissions</CardDescription>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-[#8A93A6]">
              No commissions found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-white">Date</TableHead>
                    <TableHead className="text-white">Affiliate</TableHead>
                    <TableHead className="text-white">Referred User</TableHead>
                    <TableHead className="text-white">Amount</TableHead>
                    <TableHead className="text-white">Status</TableHead>
                    <TableHead className="text-white">Source</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((commission) => (
                    <TableRow key={commission.id}>
                      <TableCell className="text-white">{new Date(commission.date).toLocaleDateString()}</TableCell>
                      <TableCell className="text-white">{commission.affiliate}</TableCell>
                      <TableCell className="text-white">{commission.referred_user}</TableCell>
                      <TableCell className="text-white">${commission.amount.toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize border-white/20 text-white">
                          {commission.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-white">{commission.source}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Alert className="bg-blue-500/10 border-blue-500/30">
        <AlertDescription className="text-sm text-blue-400">
          This page uses sample data and local persistence for Phase 0. Status updates and searching can be extended later.
        </AlertDescription>
      </Alert>
    </div>
  );
}
